import { useState } from 'react';
import { FolderOpen, Image as ImageIcon, FileText, Sparkles } from 'lucide-react';
import { FilesPage } from './FilesPage';

interface LibraryPageProps {
  brandId: string;
  onBack: () => void;
}

type Tab = 'files' | 'brand-assets' | 'creatives' | 'templates';

export function LibraryPage({ brandId, onBack }: LibraryPageProps) {
  const [tab, setTab] = useState<Tab>('files');

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 pt-4 border-b border-[#eaecf0]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-[18px] font-semibold text-[#101828]">Library</h1>
            <p className="text-[12px] text-[#667085] mt-0.5">Files, brand assets, creatives, and templates — all in one place</p>
          </div>
        </div>
        <div className="flex gap-1 -mb-px">
          <Tab id="files"        label="Files"         icon={<FolderOpen className="w-3.5 h-3.5" />} active={tab === 'files'} onClick={() => setTab('files')} />
          <Tab id="brand-assets" label="Brand assets"  icon={<ImageIcon className="w-3.5 h-3.5" />} active={tab === 'brand-assets'} onClick={() => setTab('brand-assets')} />
          <Tab id="creatives"    label="Creatives"     icon={<Sparkles className="w-3.5 h-3.5" />} active={tab === 'creatives'} onClick={() => setTab('creatives')} />
          <Tab id="templates"    label="Templates"     icon={<FileText className="w-3.5 h-3.5" />} active={tab === 'templates'} onClick={() => setTab('templates')} />
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {tab === 'files' && <FilesPage brandId={brandId} onBack={onBack} />}
        {tab === 'brand-assets' && (
          <Placeholder
            title="Brand assets"
            description="Logos, color tokens, fonts, and visual primitives from your Brand OS — coming soon as a dedicated browser. For now, manage these in Brand OS."
          />
        )}
        {tab === 'creatives' && (
          <Placeholder
            title="Creatives"
            description="Saved ad creatives, social posts, and image variants generated by your agents. Coming soon — in the meantime, find them in their parent task or chat."
          />
        )}
        {tab === 'templates' && (
          <Placeholder
            title="Templates"
            description="Reusable email templates, deck templates, and content blueprints. Coming soon."
          />
        )}
      </div>
    </div>
  );
}

function Tab({
  label, icon, active, onClick,
}: { id: string; label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-[12px] border-b-2 transition-colors ${
        active
          ? 'border-[#be43ff] text-[#be43ff] font-semibold'
          : 'border-transparent text-[#667085] hover:text-[#101828]'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Placeholder({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-center justify-center h-full px-6">
      <div className="text-center max-w-md">
        <h2 className="text-[15px] font-semibold text-[#101828] mb-2">{title}</h2>
        <p className="text-[13px] text-[#667085]">{description}</p>
      </div>
    </div>
  );
}
